/**
 * A picture slot as it looks while writing.
 *
 * The reader sees a dashed frame saying what the screenshot has to show; the
 * writer sees the same frame with both of its fields editable, because the two
 * things that make a slot work - a key that never changes and a sentence
 * describing the picture - are invisible in the markup and impossible to get
 * right by accident.
 *
 * `contentEditable={false}` on the whole node is what stops the caret from
 * entering it. Without it the writer can put a cursor inside an atom and type
 * text the document model has nowhere to keep, which reads as the editor eating
 * keystrokes.
 */
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Camera } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";

/** A slot key is a slug: lower case, digits, single hyphens. */
function slotKey(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[äã]/g, "a")
    .replace(/[öõ]/g, "o")
    .replace(/ü/g, "u")
    .replace(/š/g, "s")
    .replace(/ž/g, "z")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function ShotNodeView({ node, updateAttributes, selected }: NodeViewProps) {
  const t = useT();
  const key = String(node.attrs.key ?? "");
  const caption = String(node.attrs.caption ?? "");

  return (
    <NodeViewWrapper
      className={`crm-kb-shotnode${selected ? " is-on" : ""}`}
      contentEditable={false}
      draggable={false}
    >
      <div className="crm-kb-shotnode-mark">
        <Camera className="size-5" strokeWidth={1.7} />
      </div>
      <div className="crm-kb-shotnode-fields">
        <label>
          <span>{t("kb.shot.key")}</span>
          <input
            value={key}
            onChange={(e) => updateAttributes({ key: slotKey(e.target.value) })}
            placeholder="piletiloend"
            spellCheck={false}
          />
        </label>
        <label>
          <span>{t("kb.shot.caption")}</span>
          <input
            value={caption}
            onChange={(e) => updateAttributes({ caption: e.target.value })}
            placeholder={t("kb.shot.captionPlaceholder")}
          />
        </label>
      </div>
    </NodeViewWrapper>
  );
}
